import React, { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import ConfirmationModal from "../ui/popup/ConfirmationModal";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import SupportPopUp from "../ui/popup/SupportPopUp";

export default function CheckoutForm({
  filingUuid,
  clientSecret,
  isDetailsConfirmed = false,
}) {
  const clientBaseUrl = process.env.REACT_APP_CLIENT_DOMAIN;

  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [paymentKey, setPaymentKey] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${clientBaseUrl}/tax-filing/new-filing/${filingUuid}`,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`.
    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unexpected error occurred.");
      }
      setShowErrorModal(true);
    }

    setIsLoading(false);
  };

  const clearPaymentForm = () => {
    // Increment key to force PaymentElement to remount and clear data
    setPaymentKey((prev) => prev + 1);
  };

  const handleTryAgain = () => {
    setShowErrorModal(false);
    setErrorMessage(null);
    clearPaymentForm();
  };

  const handleContactUs = () => {
    setShowErrorModal(false);
    setShowSupportModal(true);
    clearPaymentForm();
  };

  const paymentElementOptions = {
    layout: "accordion",
  };

  return (
    <>
      <form id="payment-form" onSubmit={handleSubmit}>
        {/* Loading overlay */}
        {isLoading && (
          <div style={{ marginBottom: "20px" }}>
            <LoadingPlaceholder rows={1} type="card-large" />
          </div>
        )}

        {/* PaymentElement must stay mounted for Stripe to work */}
        <div style={{ display: isLoading ? "none" : "block" }}>
          {clientSecret && (
            <PaymentElement
              key={paymentKey}
              id="payment-element"
              options={paymentElementOptions}
            />
          )}

          <button
            className="cta-button primary"
            disabled={isLoading || !stripe || !elements || !isDetailsConfirmed}
            id="submit"
            style={{ marginTop: "20px", width: "100%" }}
          >
            <b>Submit Payment</b>
          </button>
        </div>
      </form>

      {/* Error Modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={handleContactUs}
        onConfirm={handleTryAgain}
        title="Card Declined"
        message={errorMessage || "An error occurred with your payment."}
        confirmText="Try Again"
        cancelText="Contact Us"
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
      />

      {/* Support Modal */}
      {showSupportModal && (
        <SupportPopUp setIsModalOpen={setShowSupportModal} />
      )}
    </>
  );
}
